import { useState, useEffect } from 'react';
import { Plus, FileText, Download, Euro, Calculator, User, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { api } from '../../core/apiClient';
import { formatCurrency } from '../../lib/utils';
import { ExcelFilterDropdown } from '../../components/ui/excel-filter';

interface Employee {
  id: string;
  code: string;
  firstName: string;
  lastName: string;
  baseSalary?: number;
}

interface PayrollPeriod {
  id: string;
  employeeId: string;
  employee?: Employee;
  year: number;
  month: number;
  baseSalary: number;
  extras?: number;
  bonuses?: number;
  deductions?: number;
  grossSalary: number;
  netSalary: number;
  status: string;
  documentUrl?: string;
  paidAt?: string;
}

interface PayrollConcept {
  id: string;
  code: string;
  name: string;
  type: 'DEVENGO' | 'DEDUCCION';
  isFixed: boolean;
  defaultAmount?: number;
}

const MONTHS = [
  'Gener', 'Febrer', 'Març', 'Abril', 'Maig', 'Juny',
  'Juliol', 'Agost', 'Setembre', 'Octubre', 'Novembre', 'Desembre'
];

export default function Payroll() {
  const navigate = useNavigate();
  const [payrolls, setPayrolls] = useState<PayrollPeriod[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [concepts, setConcepts] = useState<PayrollConcept[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('nomines');

  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    employeeId: '',
  });

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showConceptModal, setShowConceptModal] = useState(false);
  const [editingConcept, setEditingConcept] = useState<PayrollConcept | null>(null);

  const [generateForm, setGenerateForm] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    employeeIds: [] as string[],
  });

  const [conceptForm, setConceptForm] = useState({
    code: '',
    name: '',
    type: 'DEVENGO' as 'DEVENGO' | 'DEDUCCION',
    isFixed: true,
    defaultAmount: '',
  });

  const [stats, setStats] = useState({
    totalGross: 0,
    totalNet: 0,
    totalEmployees: 0,
    pending: 0,
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadPayrolls();
  }, [filters]);

  const loadData = async () => {
    try {
      const [empRes, conceptsRes] = await Promise.all([
        api.get<any>('/employees'),
        api.get<any>('/payroll/concepts'),
      ]);
      setEmployees(empRes.data?.data || []);
      setConcepts(conceptsRes.data?.data || []);
    } catch (error) {
      console.error('[v0] Error loading data:', error);
    }
  };

  const loadPayrolls = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('year', String(filters.year));
      params.append('month', String(filters.month));
      if (filters.employeeId) params.append('employeeId', filters.employeeId);

      const response = await api.get<any>(`/payroll?${params.toString()}`);
      const data = response.data?.data || [];
      setPayrolls(data);

      // Calculate stats
      const totalGross = data.reduce((sum: number, p: PayrollPeriod) => sum + Number(p.grossSalary), 0);
      const totalNet = data.reduce((sum: number, p: PayrollPeriod) => sum + Number(p.netSalary), 0);
      const pending = data.filter((p: PayrollPeriod) => p.status === 'PENDENT').length;

      setStats({
        totalGross,
        totalNet,
        totalEmployees: data.length,
        pending,
      });
    } catch (error) {
      console.error('[v0] Error loading payrolls:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePayrolls = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/payroll/generate', generateForm);
      setShowGenerateModal(false);
      loadPayrolls();
    } catch (error) {
      console.error('[v0] Error generating payrolls:', error);
    }
  };

  const handleMarkAsPaid = async (id: string) => {
    try {
      await api.put(`/payroll/${id}/pay`);
      loadPayrolls();
    } catch (error) {
      console.error('[v0] Error marking as paid:', error);
    }
  };

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      params.append('year', String(filters.year));
      params.append('month', String(filters.month));
      
      const response = await api.get(`/payroll/export?${params.toString()}`, {
        responseType: 'blob',
      }) as { data: Blob };
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nomines_${filters.year}_${filters.month}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('[v0] Error exporting:', error);
    }
  };

  const handleSubmitConcept = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...conceptForm,
        defaultAmount: conceptForm.defaultAmount ? parseFloat(conceptForm.defaultAmount) : undefined,
      };

      if (editingConcept) {
        await api.put(`/payroll/concepts/${editingConcept.id}`, payload);
      } else {
        await api.post('/payroll/concepts', payload);
      }

      setShowConceptModal(false);
      setEditingConcept(null);
      resetConceptForm();
      loadData();
    } catch (error) {
      console.error('[v0] Error saving concept:', error);
    }
  };

  const resetConceptForm = () => {
    setConceptForm({
      code: '',
      name: '',
      type: 'DEVENGO',
      isFixed: true,
      defaultAmount: '',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDENT':
        return <Badge className="bg-amber-100 text-amber-800">Pendent</Badge>;
      case 'PAGADA':
        return <Badge className="bg-green-100 text-green-800">Pagada</Badge>;
      case 'ANULADA':
        return <Badge className="bg-red-100 text-red-800">Anul·lada</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/rrhh')}
            title="Tornar enrere"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Nòmines</h1>
            <p className="text-slate-500">Gestió de nòmines i conceptes salarials</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportCSV}>
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
          <Button onClick={() => {
            setGenerateForm({
              year: filters.year,
              month: filters.month,
              employeeIds: [],
            });
            setShowGenerateModal(true);
          }}>
            <Calculator className="w-4 h-4 mr-2" />
            Generar Nòmines
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Brut</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.totalGross)}</p>
              </div>
              <Euro className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Net</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.totalNet)}</p>
              </div>
              <Euro className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Empleats</p>
                <p className="text-2xl font-bold">{stats.totalEmployees}</p>
              </div>
              <User className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pendents</p>
                <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
              </div>
              <FileText className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="nomines">
            <FileText className="w-4 h-4 mr-2" />
            Nòmines
          </TabsTrigger>
          <TabsTrigger value="conceptes">
            <Calculator className="w-4 h-4 mr-2" />
            Conceptes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="nomines" className="mt-4">
          {/* Filters */}
          <Card className="mb-4 rounded-xl">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label>Any</Label>
                  <select
                    className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white"
                    value={filters.year}
                    onChange={(e) => setFilters(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                  >
                    {[2024, 2025, 2026, 2027].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Mes</Label>
                  <select
                    className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white"
                    value={filters.month}
                    onChange={(e) => setFilters(prev => ({ ...prev, month: parseInt(e.target.value) }))}
                  >
                    {MONTHS.map((month, index) => (
                      <option key={index} value={index + 1}>{month}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Empleat</Label>
                  <ExcelFilterDropdown
                    label="Empleat"
                    options={employees.map(emp => ({ value: emp.id, label: `${emp.firstName} ${emp.lastName}` }))}
                    selected={filters.employeeId ? filters.employeeId.split(',') : []}
                    onApply={(values) => setFilters(prev => ({ ...prev, employeeId: values.join(',') }))}
                    className="w-full"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card className="rounded-xl">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empleat</TableHead>
                    <TableHead>Període</TableHead>
                    <TableHead className="text-right">Salari Base</TableHead>
                    <TableHead className="text-right">Extres</TableHead>
                    <TableHead className="text-right">Brut</TableHead>
                    <TableHead className="text-right">Deduccions</TableHead>
                    <TableHead className="text-right">Net</TableHead>
                    <TableHead>Estat</TableHead>
                    <TableHead className="text-right">Accions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8">Carregant...</TableCell>
                    </TableRow>
                  ) : payrolls.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                        No hi ha nòmines per aquest període
                      </TableCell>
                    </TableRow>
                  ) : (
                    payrolls.map(payroll => (
                      <TableRow key={payroll.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            {payroll.employee?.firstName} {payroll.employee?.lastName}
                          </div>
                        </TableCell>
                        <TableCell>
                          {MONTHS[payroll.month - 1]} {payroll.year}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(Number(payroll.baseSalary))}
                        </TableCell>
                        <TableCell className="text-right text-green-600">
                          +{formatCurrency(Number(payroll.extras || 0) + Number(payroll.bonuses || 0))}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(Number(payroll.grossSalary))}
                        </TableCell>
                        <TableCell className="text-right text-red-600">
                          -{formatCurrency(Number(payroll.deductions || 0))}
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          {formatCurrency(Number(payroll.netSalary))}
                        </TableCell>
                        <TableCell>{getStatusBadge(payroll.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {payroll.status === 'PENDENT' && (
                              <Button size="sm" variant="ghost" onClick={() => handleMarkAsPaid(payroll.id)}>
                                Pagar
                              </Button>
                            )}
                            {payroll.documentUrl && (
                              <Button size="sm" variant="ghost">
                                <Download className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="conceptes" className="mt-4">
          <Card className="rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Conceptes Salarials</CardTitle>
              <Button size="sm" onClick={() => { resetConceptForm(); setEditingConcept(null); setShowConceptModal(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Nou Concepte
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Codi</TableHead>
                    <TableHead>Nom</TableHead>
                    <TableHead>Tipus</TableHead>
                    <TableHead>Fix/Variable</TableHead>
                    <TableHead className="text-right">Import per defecte</TableHead>
                    <TableHead className="text-right">Accions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {concepts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No hi ha conceptes definits
                      </TableCell>
                    </TableRow>
                  ) : (
                    concepts.map(concept => (
                      <TableRow key={concept.id}>
                        <TableCell className="font-mono">{concept.code}</TableCell>
                        <TableCell className="font-medium">{concept.name}</TableCell>
                        <TableCell>
                          <Badge className={concept.type === 'DEVENGO' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {concept.type === 'DEVENGO' ? 'Meritació' : 'Deducció'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{concept.isFixed ? 'Fix' : 'Variable'}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {concept.defaultAmount ? formatCurrency(concept.defaultAmount) : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => {
                            setEditingConcept(concept);
                            setConceptForm({
                              code: concept.code,
                              name: concept.name,
                              type: concept.type,
                              isFixed: concept.isFixed,
                              defaultAmount: concept.defaultAmount ? String(concept.defaultAmount) : '',
                            });
                            setShowConceptModal(true);
                          }}>
                            Editar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Generar Nòmines */}
      <Dialog open={showGenerateModal} onOpenChange={setShowGenerateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generar Nòmines</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleGeneratePayrolls} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Any</Label>
                <select
                  className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white"
                  value={generateForm.year}
                  onChange={(e) => setGenerateForm(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                >
                  {[2024, 2025, 2026, 2027].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Mes</Label>
                <select
                  className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white"
                  value={generateForm.month}
                  onChange={(e) => setGenerateForm(prev => ({ ...prev, month: parseInt(e.target.value) }))}
                >
                  {MONTHS.map((month, index) => (
                    <option key={index} value={index + 1}>{month}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label>Empleats</Label>
              <p className="text-sm text-slate-500 mb-2">
                Deixa buit per generar per tots els empleats actius
              </p>
              <select
                multiple
                className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white h-40"
                value={generateForm.employeeIds}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, option => option.value);
                  setGenerateForm(prev => ({ ...prev, employeeIds: selected }));
                }}
              >
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.code} - {emp.firstName} {emp.lastName}
                  </option>
                ))}
              </select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowGenerateModal(false)}>
                Cancel·lar
              </Button>
              <Button type="submit">Generar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal Concepte */}
      <Dialog open={showConceptModal} onOpenChange={setShowConceptModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingConcept ? 'Editar Concepte' : 'Nou Concepte'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitConcept} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Codi *</Label>
                <Input
                  value={conceptForm.code}
                  onChange={(e) => setConceptForm(prev => ({ ...prev, code: e.target.value }))}
                  placeholder="SAL-BASE"
                  required
                />
              </div>
              <div>
                <Label>Tipus *</Label>
                <select
                  className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white"
                  value={conceptForm.type}
                  onChange={(e) => setConceptForm(prev => ({ ...prev, type: e.target.value as any }))}
                >
                  <option value="DEVENGO">Meritació (+)</option>
                  <option value="DEDUCCION">Deducció (-)</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Nom *</Label>
              <Input
                value={conceptForm.name}
                onChange={(e) => setConceptForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Salari Base"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Import per defecte</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={conceptForm.defaultAmount}
                  onChange={(e) => setConceptForm(prev => ({ ...prev, defaultAmount: e.target.value }))}
                />
              </div>
              <div className="flex items-end gap-2">
                <input
                  type="checkbox"
                  id="isFixed"
                  checked={conceptForm.isFixed}
                  onChange={(e) => setConceptForm(prev => ({ ...prev, isFixed: e.target.checked }))}
                />
                <Label htmlFor="isFixed">Import fix</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowConceptModal(false)}>
                Cancel·lar
              </Button>
              <Button type="submit">
                {editingConcept ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
